
import React, { useState } from 'react';
import { Identity, CountryCode } from '../types';
import { Download, FileJson, Table as TableIcon, CheckSquare, Square, FileText } from 'lucide-react';

interface Props {
  history: Identity[];
  lang: string;
  onUpdateHistory: () => void;
}

const getIdLabel = (country: string): string => {
  switch (country) {
    case CountryCode.US: return 'SSN';
    case CountryCode.CN: return '身份证号';
    case CountryCode.RU: return 'СНИЛС';
    default: return 'ID';
  }
};

const BulkExtract: React.FC<Props> = ({ history }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === history.length && history.length > 0) setSelectedIds(new Set());
    else setSelectedIds(new Set(history.map(h => h.id)));
  };

  const getSelectedData = () => history.filter(h => selectedIds.has(h.id));

  const downloadFile = (content: string, fileName: string, contentType: string) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([content], { type: contentType }));
    a.download = fileName;
    a.click();
  };

  const exportAsJSON = () => downloadFile(JSON.stringify(getSelectedData(), null, 2), 'export.json', 'application/json');

  return (
    <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[700px] animate-in fade-in slide-in-from-bottom-4">
      <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div><h2 className="text-xl font-black text-slate-900">批量提取</h2></div>
        <div className="flex items-center gap-2">
          <button onClick={exportAsJSON} aria-disabled={selectedIds.size === 0} disabled={selectedIds.size === 0} className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 disabled:opacity-50 transition-all shadow-lg">导出 JSON</button>
        </div>
      </div>
      <div className="flex-1 overflow-auto custom-scrollbar">
        {history.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400" role="status"><Download size={48} className="mb-4 opacity-20" /><p className="font-bold">暂无数据</p></div>
        ) : (
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-white z-10 border-b border-slate-100">
              <tr>
                <th className="p-4 w-12 text-center">
                  <button type="button" onClick={toggleSelectAll} aria-label={selectedIds.size === history.length ? "取消全选" : "全选所有项目"} className="text-blue-600">
                    {selectedIds.size === history.length ? <CheckSquare size={20} /> : <Square size={20} />}
                  </button>
                </th>
                <th className="p-4 text-[10px] font-bold text-slate-600 uppercase tracking-widest">档案对象</th>
                <th className="p-4 text-[10px] font-bold text-slate-600 uppercase tracking-widest">信息摘要</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {history.map((h) => (
                <tr key={h.id} onClick={() => toggleSelect(h.id)} className={`group cursor-pointer hover:bg-blue-50/30 transition-colors ${selectedIds.has(h.id) ? 'bg-blue-50/50' : ''}`}>
                  <td className="p-4 text-center">
                    <button type="button" aria-label={`选择项目: ${h.name}`} aria-pressed={selectedIds.has(h.id)} className={selectedIds.has(h.id) ? 'text-blue-600' : 'text-slate-300 group-hover:text-slate-400'}>
                      {selectedIds.has(h.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                    </button>
                  </td>
                  <td className="p-4"><p className="text-sm font-bold text-slate-800">{h.name}</p></td>
                  <td className="p-4"><p className="text-[10px] font-mono text-slate-500">{getIdLabel(h.address.country)}: {h.idNumber}</p></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BulkExtract;
